/**
 * Share Milestone Hook
 * Provides a context and hook for triggering milestone sharing throughout the app
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { ShareModal } from './ShareModal';
import { useAuth } from '@/context/AuthContext';

// Context for sharing
const ShareMilestoneContext = createContext(null);

/**
 * Hook to access milestone sharing functionality
 * @returns {Object} Share milestone methods
 */
export const useShareMilestone = () => {
  const context = useContext(ShareMilestoneContext);
  if (!context) {
    throw new Error('useShareMilestone must be used within ShareMilestoneProvider');
  }
  return context;
};

/**
 * Provider component that wraps the app to enable milestone sharing
 */
export const ShareMilestoneProvider = ({ children }) => {
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [milestoneType, setMilestoneType] = useState('badge');
  const [milestoneData, setMilestoneData] = useState({});
  
  const userName = profile?.display_name || profile?.full_name || user?.email?.split('@')[0] || 'Campus Explorer';
  
  /**
   * Share a badge earned
   */
  const shareBadge = useCallback((badge) => {
    setMilestoneType('badge');
    setMilestoneData({
      badgeName: badge.name || badge.badge_name,
      badgeDescription: badge.description,
      badgeIcon: badge.icon_name || badge.icon || 'award',
      badgeTier: badge.tier || badge.color || 'gold',
      earnedAt: badge.earned_at || new Date().toISOString(),
    });
    setIsOpen(true);
  }, []);
  
  /**
   * Share a leaderboard rank
   */
  const shareRank = useCallback((rankData) => {
    setMilestoneType('rank');
    setMilestoneData({
      rank: rankData.rank || rankData.rank_position,
      totalUsers: rankData.total || rankData.total_users || 100,
      score: rankData.score || rankData.points || 0,
      leaderboardType: rankData.type || 'student',
      period: rankData.period || 'All Time',
      campusName: rankData.campus_name,
    });
    setIsOpen(true);
  }, []);
  
  /**
   * Share events milestone
   */
  const shareEventsMilestone = useCallback((eventsData) => {
    // Calculate milestone reached
    const count = eventsData.count || eventsData.events_count || 0;
    let milestone = 1;
    [1, 5, 10, 25, 50, 100, 250, 500].forEach(m => {
      if (count >= m) milestone = m;
    });
    
    setMilestoneType('events');
    setMilestoneData({
      eventsCount: count,
      milestone,
      totalCheckIns: eventsData.check_ins || eventsData.total_checkins,
    });
    setIsOpen(true);
  }, []);
  
  /**
   * Share VIP status
   */
  const shareVIPStatus = useCallback((statusData) => {
    setMilestoneType('vip');
    setMilestoneData({
      statusName: statusData.name || 'VIP',
      statusDescription: statusData.description || 'Exclusive access to premium events and perks',
      benefits: statusData.benefits || [
        'Priority event access',
        'Exclusive member events',
        'Premium support',
      ],
    });
    setIsOpen(true);
  }, []);
  
  /**
   * Share passport milestone
   */
  const sharePassportMilestone = useCallback((passportData) => {
    setMilestoneType('passport');
    setMilestoneData({
      milestoneType: passportData.type || 'events', // 'badges' | 'stamps' | 'events' | 'streak'
      milestoneValue: passportData.value || passportData.count || 0,
      milestoneName: passportData.name || passportData.label,
    });
    setIsOpen(true);
  }, []);
  
  /**
   * Generic share method
   */
  const shareMilestone = useCallback((type, data) => {
    setMilestoneType(type);
    setMilestoneData(data);
    setIsOpen(true);
  }, []);
  
  const closeShare = useCallback(() => {
    setIsOpen(false);
    setMilestoneData({});
  }, []);
  
  const value = {
    // Individual share methods
    shareBadge,
    shareRank,
    shareEventsMilestone,
    shareVIPStatus,
    sharePassportMilestone,
    // Generic share
    shareMilestone,
    // State
    isShareOpen: isOpen,
    closeShare,
  };
  
  return (
    <ShareMilestoneContext.Provider value={value}>
      {children}
      <ShareModal
        isOpen={isOpen}
        onClose={closeShare}
        milestoneType={milestoneType}
        milestoneData={milestoneData}
        userName={userName}
      />
    </ShareMilestoneContext.Provider>
  );
};

export default useShareMilestone;
